import { LOG_LEVEL } from '@b2x/shared';
import { type DispatchFunction, SeamlessConnection } from '@seamless/store';
import type { AnyAction, Reducer } from 'redux';

import type {
  BusinessContextParams,
  ContextParams,
  ProfileContextParams,
  ProfileDispatchers,
  ProfileRefetchParams,
  ProfileState,
  ProfileStateParams,
  SDS,
} from '../../types/profile-type';

import { ProfileSource } from '../../types/enums';
import { ACTION_SET_STATE, PROFILE_CONNECTION, PROFILE_PERSISTED_CONNECTION } from '../../utilities/constants';
import { PL_LOG_GROUPS, logger } from '../../utilities/logger';
import { refetchProfiles } from '../fetch';
import { defineActiveProfile, persistProfileState } from '../profile';

// eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars, @typescript-eslint/noEmptyBlockStatements
const noop = (..._args: any[]) => {};

export class ProfileConnection extends SeamlessConnection<ProfileDispatchers, ProfileState> {
  private defaultState!: ProfileState;

  get initialState(): ProfileState {
    return Object.seal({ ...this.defaultState });
  }

  set initialState(profileState: ProfileState) {
    this.defaultState = { ...profileState };

    persistProfileState(PROFILE_PERSISTED_CONNECTION, profileState);
  }

  constructor(state: ProfileState) {
    super(PROFILE_CONNECTION);

    this.initialState = state;
  }

  getReducer(): Reducer {
    return (state: ProfileState, action: AnyAction) => {
      switch (action.type) {
        case this.getActionType(ACTION_SET_STATE): {
          const currentState: ProfileState = { ...state, ...action.payload };

          return currentState;
        }
        default:
          return state;
      }
    };
  }

  getPublicDispatchers(): ProfileDispatchers {
    logger('\n\n\n 🚨 Using deprecated dispatchers!', {
      args: [
        '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
        '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
      ],
      level: LOG_LEVEL.WARN,
      group: PL_LOG_GROUPS.CONNECTION,
    });

    return {
      setBusinessType: (options: BusinessContextParams) => {
        logger('\n\n\n 🚨 Using deprecated setBusinessType dispatcher!', {
          args: [
            '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
            '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
          ],
          level: LOG_LEVEL.WARN,
          group: PL_LOG_GROUPS.CONNECTION,
        });

        return (dispatch: DispatchFunction, getState: () => SDS) => {
          this.setContextAction({ dispatch, getState, options });
        };
      },

      setActiveProfile: (options: ProfileContextParams) => {
        logger('\n\n\n 🚨 Using deprecated setActiveProfile dispatcher!', {
          args: [
            '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
            '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
          ],
          level: LOG_LEVEL.WARN,
          group: PL_LOG_GROUPS.CONNECTION,
        });

        return (dispatch: DispatchFunction, getState: () => SDS) => {
          this.setContextAction({ dispatch, getState, options });
        };
      },

      setStateProfile: (profileStateParams: ProfileStateParams) => {
        return (dispatch: DispatchFunction) => {
          logger('\n\n\n 🚨 Using deprecated setStateProfile dispatcher!', {
            args: [
              '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
              '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
            ],
            level: LOG_LEVEL.WARN,
            group: PL_LOG_GROUPS.CONNECTION,
          });

          const { activeProfile, availableProfiles, source, meta } = profileStateParams;

          dispatch(this.getAction(ACTION_SET_STATE, { activeProfile, availableProfiles, source, meta }));
        };
      },

      setRefetchProfiles: (options: ProfileRefetchParams, fn = noop) => {
        return async (dispatch: DispatchFunction) => {
          logger('\n\n\n 🚨 Using deprecated setRefetchProfiles dispatcher!', {
            args: [
              '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
              '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
            ],
            level: LOG_LEVEL.WARN,
            group: PL_LOG_GROUPS.CONNECTION,
          });

          const { businessType, meId = null, source = ProfileSource.PAL } = options;
          const [profileState, error] = await refetchProfiles(businessType, meId, source);

          dispatch(this.getAction(ACTION_SET_STATE, profileState));
          dispatch(() => fn(error ?? undefined));

          logger('Refetch Profiles', {
            args: [options, error, profileState],
            level: LOG_LEVEL.WARN,
            group: PL_LOG_GROUPS.CONNECTION,
          });
        };
      },
    };
  }

  public async onStateChange(state: ProfileState): Promise<void> {
    logger('Profile state changed and persisted', {
      args: [state],
      level: LOG_LEVEL.WARN,
      group: PL_LOG_GROUPS.CONNECTION,
    });

    persistProfileState(PROFILE_PERSISTED_CONNECTION, state);

    return Promise.resolve();
  }

  private setContextAction(params: { dispatch: DispatchFunction; getState: () => SDS; options: ContextParams }) {
    const { dispatch, getState, options } = params;
    const { source = ProfileSource.USER, businessType, profileID = null } = options;
    const { B2XCORE_PROFILE: profileState } = getState();

    const activeProfile = defineActiveProfile(
      { businessType, profileId: profileID } as any,
      profileState.availableProfiles,
    );

    logger('Context changed:', {
      args: [
        `Source: ${source} |`,
        `New: ${activeProfile.businessType} |`,
        `Old: ${profileState?.activeProfile.businessType}`,
      ],
      group: PL_LOG_GROUPS.CONNECTION,
      level: LOG_LEVEL.INFO,
    });

    dispatch(
      this.getAction(ACTION_SET_STATE, {
        activeProfile,
        availableProfiles: profileState.availableProfiles,
        source,
        meta: profileState.meta,
      }),
    );
  }
}
